import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useCurrency } from '../../../features/currency/CurrencyContext';

const FALLBACK_SLIDES = [
  {
    image: '/assets/images/slider.png',
    badge: 'Verified agents · Trusted listings',
    badgeIcon: 'ShieldCheck',
    title: 'Find your next home with confidence',
    highlight: 'anywhere in the world',
    subtitle:
      'Browse verified rentals, sales, and land — or hire a professional agent to hunt for you.',
  },
  {
    image: null,
    badge: 'Specific Property Request',
    badgeIcon: 'ClipboardList',
    title: 'Tell us exactly what you need',
    highlight: 'agents respond',
    subtitle:
      'Post budget, location, and must-haves. Verified real estate agents match you with real inventory.',
  },
];

const HeroSlider = ({
  slides = [],
  categories = [],
  searchQuery = '',
  onSearchChange,
  onSearch,
}) => {
  const navigate = useNavigate();
  const { currency, formatPrice, usdToUgx } = useCurrency();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [transactionType, setTransactionType] = useState('rent');
  const [selectedPropertyType, setSelectedPropertyType] = useState('all');
  const [bedrooms, setBedrooms] = useState('any');
  const [maxBudget, setMaxBudget] = useState('');
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const slideRef = useRef(null);

  const displaySlides = useMemo(() => {
    if (Array.isArray(slides) && slides.length > 0) {
      return slides.map((slide, i) => {
        const fallback = FALLBACK_SLIDES[i % FALLBACK_SLIDES.length];
        return {
          image: slide.image_url || slide.image || fallback.image,
          badge: slide.badge || fallback.badge,
          badgeIcon: slide.badgeIcon || fallback.badgeIcon,
          title: slide.titlePrefix || slide.title || fallback.title,
          highlight: slide.titleHighlight || fallback.highlight,
          subtitle: slide.subtitle || slide.description || fallback.subtitle,
        };
      });
    }
    return FALLBACK_SLIDES;
  }, [slides]);

  useEffect(() => {
    if (displaySlides.length <= 1 || !isAutoPlaying) return undefined;
    const id = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % displaySlides.length);
    }, 8000);
    return () => clearInterval(id);
  }, [displaySlides.length, isAutoPlaying]);

  const nextSlide = () => setCurrentSlide((p) => (p + 1) % displaySlides.length);
  const prevSlide = () =>
    setCurrentSlide((p) => (p - 1 + displaySlides.length) % displaySlides.length);

  const handleTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) nextSlide();
    if (distance < -50) prevSlide();
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    // Convert budget from display currency to UGX for search if needed
    let budget = maxBudget;
    if (budget && currency === 'USD') {
      const ugx = Math.round(Number(String(budget).replace(/[^0-9.]/g, '')) * usdToUgx);
      budget = String(ugx);
    } else if (budget) {
      budget = String(budget).replace(/[^0-9]/g, '');
    }
    onSearch?.({
      location: searchQuery,
      transactionType,
      categoryId: selectedPropertyType,
      bedrooms,
      maxBudget: budget,
    });
  };

  const active = displaySlides[currentSlide] || displaySlides[0];

  return (
    <section
      ref={slideRef}
      className="relative w-full min-h-[640px] lg:min-h-[680px] overflow-hidden bg-slate-950"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background slides */}
      {displaySlides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={index !== currentSlide}
        >
          {slide.image ? (
            <Image
              src={slide.image}
              alt=""
              className={`w-full h-full object-cover transition-transform duration-[10000ms] ease-out ${
                index === currentSlide ? 'scale-100' : 'scale-105'
              }`}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-sky-950" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-slate-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/30" />
        </div>
      ))}

      {/* Decorative glow */}
      <div className="pointer-events-none absolute -left-24 top-20 w-72 h-72 rounded-full bg-sky-500/15 blur-3xl" />
      <div className="pointer-events-none absolute right-0 bottom-0 w-96 h-96 rounded-full bg-[#ff6f23]/10 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 lg:pt-16 lg:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-md text-xs font-semibold tracking-wide mb-5">
              <Icon name={active.badgeIcon || 'ShieldCheck'} size={14} className="text-sky-300" />
              <span className="text-white/90">{active.badge}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-4">
              {active.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 to-[#ff6f23]">
                {active.highlight}
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-8">
              {active.subtitle}
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <Button
                size="lg"
                className="bg-white text-slate-900 hover:bg-sky-50 font-semibold px-6 shadow-xl"
                onClick={() => navigate('/property-search-browse')}
              >
                <Icon name="Search" size={18} className="mr-2" />
                Browse properties
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/25 text-white hover:bg-white/10 font-semibold px-6"
                onClick={() => navigate('/notice-board')}
              >
                <Icon name="ClipboardList" size={18} className="mr-2" />
                Specific Property Request
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { value: 'UGX / USD', label: 'Live currency' },
                { value: 'Verified', label: 'Agent network' },
                { value: '24/7', label: 'Marketplace' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm px-3 py-3"
                >
                  <p className="text-sm sm:text-base font-bold text-white">{stat.value}</p>
                  <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Search card */}
          <div className="lg:col-span-6">
            <form
              onSubmit={handleSearchSubmit}
              className="relative rounded-3xl border border-white/15 bg-white shadow-2xl shadow-black/30 overflow-hidden"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-500 via-[#ff6f23] to-violet-500" />

              <div className="p-5 sm:p-7">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700 mb-1">
                      Smart search
                    </p>
                    <h2 className="text-xl font-bold text-slate-900">Find properties</h2>
                    <p className="text-xs text-slate-500 mt-1">
                      Prices shown in {currency} · 1 USD ≈ {Number(usdToUgx).toLocaleString()} UGX
                    </p>
                  </div>
                  <div className="hidden sm:flex w-11 h-11 rounded-2xl bg-slate-900 text-white items-center justify-center">
                    <Icon name="Home" size={20} />
                  </div>
                </div>

                {/* Rent / Sale */}
                <div className="grid grid-cols-2 p-1 bg-slate-100 rounded-xl mb-4">
                  <button
                    type="button"
                    className={`py-2.5 text-sm font-semibold rounded-lg transition-all ${
                      transactionType === 'rent'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    onClick={() => setTransactionType('rent')}
                  >
                    For Rent
                  </button>
                  <button
                    type="button"
                    className={`py-2.5 text-sm font-semibold rounded-lg transition-all ${
                      transactionType === 'sell'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    onClick={() => setTransactionType('sell')}
                  >
                    For Sale
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                      <Icon name="MapPin" size={13} className="text-slate-400" />
                      Location / area
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => onSearchChange?.(e.target.value)}
                      placeholder="e.g. Kololo, Kampala, Wakiso"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                        <Icon name="Building2" size={13} className="text-slate-400" />
                        Type
                      </label>
                      <select
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        value={selectedPropertyType}
                        onChange={(e) => setSelectedPropertyType(e.target.value)}
                      >
                        <option value="all">All types</option>
                        {(categories.length
                          ? categories
                          : [
                              { id: 'apartment', category: 'Apartment' },
                              { id: 'house', category: 'House' },
                              { id: 'land', category: 'Land' },
                            ]
                        ).map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.category}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                        <Icon name="BedDouble" size={13} className="text-slate-400" />
                        Bedrooms
                      </label>
                      <select
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                        value={bedrooms}
                        onChange={(e) => setBedrooms(e.target.value)}
                      >
                        <option value="any">Any</option>
                        <option value="1">1+</option>
                        <option value="2">2+</option>
                        <option value="3">3+</option>
                        <option value="4">4+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                      <Icon name="Coins" size={13} className="text-slate-400" />
                      Max budget ({currency})
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={maxBudget}
                        onChange={(e) =>
                          setMaxBudget(e.target.value.replace(/[^0-9.]/g, ''))
                        }
                        placeholder={
                          currency === 'USD' ? 'e.g. 800' : 'e.g. 2500000'
                        }
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 pr-16 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-slate-400">
                        {currency}
                      </span>
                    </div>
                    {maxBudget && (
                      <p className="text-[11px] text-slate-500 mt-1.5">
                        ≈{' '}
                        {currency === 'USD'
                          ? formatPrice(Number(maxBudget) * usdToUgx, 'UGX')
                          : formatPrice(maxBudget, 'UGX')}{' '}
                        max
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3.5 text-sm transition-colors shadow-lg shadow-slate-900/20"
                  >
                    <Icon name="Search" size={16} />
                    Search matching properties
                  </button>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Icon name="ShieldCheck" size={12} className="text-emerald-500" />
                    Verified professionals
                  </span>
                  <Link
                    to="/pro/register"
                    className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-slate-900"
                  >
                    Are you an agent?
                    <Icon name="ArrowRight" size={12} />
                  </Link>
                </div>
              </div>
            </form>

            {/* App mini strip */}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md px-4 py-3">
              <div className="flex items-center gap-3 text-white">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
                  <Icon name="Smartphone" size={16} className="text-sky-300" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Get the HireBroker app</p>
                  <p className="text-[11px] text-slate-400">Chat agents · save homes · book visits</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href="https://apps.apple.com/ug/app/hire-broker/id6476240355"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-black/80 text-white px-3 py-2 text-[11px] font-semibold hover:bg-black"
                >
                  <Icon name="Apple" size={14} />
                  App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.hirebroker.app&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white text-slate-900 px-3 py-2 text-[11px] font-semibold hover:bg-sky-50"
                >
                  <Icon name="Play" size={12} />
                  Google Play
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      {displaySlides.length > 1 && (
        <>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {displaySlides.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIsAutoPlaying((v) => !v)}
            className="absolute top-6 right-6 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/15 backdrop-blur-md flex items-center justify-center"
            aria-label={isAutoPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            <Icon name={isAutoPlaying ? 'Pause' : 'Play'} size={14} />
          </button>
        </>
      )}
    </section>
  );
};

export default HeroSlider;
